"use client";
import _extends from '@babel/runtime/helpers/extends';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import * as React from 'react';
import { wrapper, tabs } from './styles.js';
import { Tab } from './Tab/Tab.web.js';
import { useHorizontalScroll, handleTabKeyDown } from './utils.web.js';
import { ThemeProvider } from 'styled-components';
import { useIsomorphicLayoutEffect } from '../utils/hooks/useIsoMorphicLayoutEffect.web.js';
import { IconButton } from '../IconButton/IconButton.web.js';
import { Box } from '../Box/Box.web.js';
import { useSmartControlled } from '../utils/hooks/useSmartControlled.js';
import { useTheme } from '../utils/theming/useTheme.js';
import { IconChevronLeft } from '../icons/ui_navigation/IconChevronLeft.web.js';
import { setTransitionProps } from '../utils/helpers/style.js';
import { timings } from '../constants/timings.js';
import { IconChevronRight } from '../icons/ui_navigation/IconChevronRight.web.js';

var _excluded = ["items", "withBorder", "underlineColor", "backgroundColor", "textColor", "textSize", "active", "onTabChange", "variant", "transparent", "tabPanelProps", "aria-label", "id"];
function hasContent(items) {
  return items[0].content !== undefined;
}
function Tabs(props) {
  var items = props.items,
    withBorder = props.withBorder,
    underlineColor = props.underlineColor,
    backgroundColor = props.backgroundColor,
    textColor = props.textColor,
    textSize = props.textSize,
    activeProp = props.active,
    onTabChangeProp = props.onTabChange,
    variantProp = props.variant,
    _props$transparent = props.transparent,
    transparent = _props$transparent === undefined ? false : _props$transparent,
    tabPanelProps = props.tabPanelProps,
    _props$ariaLabel = props['aria-label'],
    ariaLabel = _props$ariaLabel === undefined ? 'Tabs' : _props$ariaLabel,
    id = props.id,
    rest = _objectWithoutProperties(props, _excluded);
  var _useSmartControlled = useSmartControlled(items[0].id, activeProp, onTabChangeProp),
    _useSmartControlled2 = _slicedToArray(_useSmartControlled, 2),
    active = _useSmartControlled2[0],
    onTabChange = _useSmartControlled2[1];
  var initialRender = React.useRef(true);
  var wrapperRef = React.useRef(null);
  var _useHorizontalScroll = useHorizontalScroll(wrapperRef),
    scrolling = _useHorizontalScroll.scrolling,
    scrollEndLeft = _useHorizontalScroll.scrollEndLeft,
    scrollEndRight = _useHorizontalScroll.scrollEndRight,
    handleScrolling = _useHorizontalScroll.handleScrolling;

  // store refs to a map by item, used to focus next/previous on arrow nav
  var refMap = React.useMemo(function () {
    var map = new Map();
    items.forEach(function (item) {
      return map.set(item, /*#__PURE__*/React.createRef());
    });
    return map;
  }, [items]);
  var scrollActiveTabIntoView = React.useCallback(function (smooth) {
    var activeItem = items.find(function (item) {
      return item.id === active;
    });

    // istanbul ignore next: not every testable
    var activeRef = activeItem && refMap.get(activeItem) || {
      current: null
    };

    // istanbul ignore next: typescript guard
    if (!wrapperRef.current || !activeRef.current) return;
    var x = activeRef.current.offsetLeft;
    var width = activeRef.current.offsetWidth;
    var tabCenter = x + width / 2;
    var containerWidth = wrapperRef.current.offsetWidth;

    // istanbul ignore next: jest doesn't like element.scrollTo
    if (wrapperRef.current.scrollTo) {
      wrapperRef.current.scrollTo({
        left: tabCenter - containerWidth / 2,
        behavior: smooth ? 'smooth' : 'auto'
      });
    }
  }, [active, items, refMap]);

  // Scroll active tab into view when active tab changes
  useIsomorphicLayoutEffect(function () {
    scrollActiveTabIntoView(!initialRender.current);
    initialRender.current = false;
  }, [initialRender, scrollActiveTabIntoView]);

  // istanbul ignore next -- scrolling is not testable with sensible effort
  var handleScrollClick = React.useCallback(function (direction) {
    var _wrapperRef$current;
    if (wrapperRef.current === null) {
      return;
    }
    var scrollAmount = wrapperRef.current.clientWidth * 0.5;
    (_wrapperRef$current = wrapperRef.current) === null || _wrapperRef$current === undefined || _wrapperRef$current.scrollTo({
      left: wrapperRef.current.scrollLeft + scrollAmount * (direction === 'left' ? -1 : 1),
      behavior: 'smooth'
    });
  }, [wrapperRef]);
  var theme = useTheme();
  var isControlledContent = hasContent(items);
  return /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: theme
  }, /*#__PURE__*/React.createElement(wrapper, _extends({
    id: id,
    "data-testid": id ? "elemental-Tabs-".concat(id) : 'elemental-Tabs',
    $withBorder: withBorder,
    $transparent: transparent,
    visibility: initialRender.current ? 'hidden' : 'visible'
  }, rest),
  // istanbul ignore next -- scrolling is not testable with sensible effort
  scrolling && /*#__PURE__*/React.createElement(IconButton, _extends({
    icon: /*#__PURE__*/React.createElement(IconChevronLeft, null),
    status: "plain",
    color: theme.colors.text,
    height: "auto",
    onClick: handleScrollClick.bind(null, 'left'),
    opacity: scrollEndLeft ? 0 : 1,
    visibility: scrollEndLeft ? 'hidden' : 'visible'
  }, setTransitionProps({
    opacity: timings.slower
  }))), /*#__PURE__*/React.createElement(Box, {
    position: "relative",
    overflow: "hidden"
  }, /*#__PURE__*/React.createElement(tabs, {
    "data-testid": "elemental-Tabs-inner",
    role: "tablist",
    ref: wrapperRef,
    onScroll: handleScrolling,
    "aria-label": ariaLabel,
    $scrollEndLeft: scrollEndLeft,
    $scrollEndRight: scrollEndRight,
    $scrolling: scrolling,
    $backgroundColor: backgroundColor,
    $transparent: transparent
  }, items.map(function (item) {
    return /*#__PURE__*/React.createElement(Tab, {
      variant: variantProp,
      "data-testid": "elemental-Tab-".concat(item.id),
      key: item.id,
      item: item,
      isActive: active === item.id,
      transparent: transparent,
      onKeyDown: function onKeyDown(e) {
        return handleTabKeyDown(e, item, items, onTabChange, refMap);
      },
      ref: refMap.get(item),
      onClick: onTabChange,
      textSize: textSize,
      underlineColor: underlineColor,
      textColor: textColor
    });
  }))),
  // istanbul ignore next -- scrolling is not testable with sensible effort
  scrolling && /*#__PURE__*/React.createElement(IconButton, _extends({
    icon: /*#__PURE__*/React.createElement(IconChevronRight, null),
    status: "plain",
    height: "auto",
    color: theme.colors.text,
    onClick: handleScrollClick.bind(null, 'right'),
    opacity: scrollEndRight ? 0 : 1,
    visibility: scrollEndRight ? 'hidden' : 'visible'
  }, setTransitionProps({
    opacity: timings.slower
  })))), isControlledContent && items.map(function (item) {
    return /*#__PURE__*/React.createElement(Box, _extends({
      tag: "section",
      role: "tabpanel",
      "data-testid": "elemental-TabPanel-".concat(item.id),
      key: item.id,
      id: item.id.toString(),
      hidden: active !== item.id,
      "aria-labelledby": "".concat(item.id, "-tab")
    }, tabPanelProps), item.content);
  }));
}

export { Tab, Tabs };
